import * as styles from "./CmsMap.module.scss";

import { Circle, GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import CmsMapOverlayCard from "../../../components/SignPostCards/CmsMapOverlayCard/CmsMapOverlayCard";
import { useCmsMap } from "../../../context";
import { withSkipMap } from "../../../hoc/withSkipMap";
import { Colours } from "../../../utils/colours";
import { GOOGLE_API_LIBRARIES } from "../../../utils/google";
import CmsMarker from "../CmsMarker/CmsMarker";

const containerStyle: React.CSSProperties = {
	position: "relative",
	width: "100%",
	height: "100%",
};

const center = {
	lat: -2.605056,
	lng: 51.4555904,
};

const CmsMap: React.FC = () => {
	const {
		userLocation,
		currentDistance,
		cmsMapLocationsInRange,
		cmsMapLocationsLocations,
		onSelectCmsMap,
		selectedCmsMapName,
	} = useCmsMap();

	const [map, setMap] = useState<google.maps.Map>(null);

	const onLoad = useCallback(
		function callback(callbackMap: google.maps.Map) {
			window.google.maps.event.addListenerOnce(callbackMap, "idle", () => {
				document.querySelector<HTMLElement>("#CmsMapContainerId iframe").title =
					"Map";
			});
			if (!cmsMapLocationsLocations?.length) return setMap(callbackMap);
			const bounds = new window.google.maps.LatLngBounds(
				new window.google.maps.LatLng({
					lat: Number.parseFloat(cmsMapLocationsLocations?.[0]?.field_latitude),
					lng: Number.parseFloat(
						cmsMapLocationsLocations?.[0]?.field_longitude,
					),
				}),
			);
			for (const mapItem of cmsMapLocationsLocations) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: Number.parseFloat(mapItem.field_latitude),
						lng: Number.parseFloat(mapItem.field_longitude),
					}),
				);
			}
			callbackMap.fitBounds(bounds);
			setMap(callbackMap);
		},
		[cmsMapLocationsLocations],
	);

	const onUnmount = useCallback(function callback() {
		setMap(null);
	}, []);

	const getFitBounds = useCallback(() => {
		if (cmsMapLocationsInRange?.length > 0 && map) {
			const bounds = new window.google.maps.LatLngBounds(
				new window.google.maps.LatLng({
					lat: Number.parseFloat(cmsMapLocationsInRange[0].field_latitude),
					lng: Number.parseFloat(cmsMapLocationsInRange[0].field_longitude),
				}),
			);
			for (const mapItem of cmsMapLocationsInRange) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: Number.parseFloat(mapItem.field_latitude),
						lng: Number.parseFloat(mapItem.field_longitude),
					}),
				);
			}
			if (userLocation?.latitude) {
				bounds.extend(
					new window.google.maps.LatLng({
						lat: userLocation.latitude,
						lng: userLocation.longitude,
					}),
				);
			}
			map.fitBounds(bounds);
		}
	}, [cmsMapLocationsInRange, map, userLocation]);

	useEffect(() => {
		getFitBounds();
		window.addEventListener("resize", getFitBounds);

		return () => window.removeEventListener("resize", getFitBounds);
	}, [getFitBounds]);

	const selectedCmsMap = useMemo(
		() =>
			cmsMapLocationsLocations.find(
				(cmsMap) => cmsMap.title === selectedCmsMapName,
			),
		[cmsMapLocationsLocations, selectedCmsMapName],
	);

	const { isLoaded: googleIsLoaded } = useJsApiLoader({
		id: "google-map-script",
		googleMapsApiKey: process.env.GATSBY_GOOGLE_API_KEY,
		libraries: GOOGLE_API_LIBRARIES,
	});

	const userLatLng = useMemo(
		() =>
			!googleIsLoaded || !userLocation
				? null
				: new window.google.maps.LatLng({
						lat: userLocation.latitude,
						lng: userLocation.longitude,
					}),
		[googleIsLoaded, userLocation],
	);
	return (
		<div id="cmsmap" className={styles.mapcontainer}>
			<div className={styles.mapwrapper}>
				{googleIsLoaded && (
					<GoogleMap
						id="CmsMapContainerId"
						options={{
							fullscreenControl: false,
							maxZoom: 8,
							mapId: "b2a09d2327650d38",
							mapTypeControl: false,
							streetViewControl: false,
						}}
						mapContainerStyle={containerStyle}
						center={center}
						onLoad={onLoad}
						onUnmount={onUnmount}
						onClick={() => onSelectCmsMap(null)}
					>
						{cmsMapLocationsInRange?.map((cmsMap) => (
							<CmsMarker
								key={cmsMap.title}
								isSelected={cmsMap.title === selectedCmsMapName}
								onClick={() => onSelectCmsMap(cmsMap.title)}
								latitude={Number.parseFloat(cmsMap.field_latitude)}
								longitude={Number.parseFloat(cmsMap.field_longitude)}
								iconUrl={cmsMap}
								selectedIconUrl={cmsMap}
								title={cmsMap.field_map_card_location}
							/>
						))}
						{currentDistance && userLocation?.latitude && (
							<>
								<Circle
									center={userLatLng}
									radius={currentDistance * 1600}
									options={{
										fillColor: Colours.BLUE_SKY_DARK,
										fillOpacity: 0.2,
										strokeColor: Colours.BLUE_SKY_DARK,
										strokeOpacity: 0.5,
										strokeWeight: 1,
										map: map,
										visible: true,
									}}
								/>
								<Circle
									center={userLatLng}
									radius={10}
									options={{
										fillColor: Colours.BLUE_SKY_DARK,
										fillOpacity: 1,
										strokeColor: Colours.BLUE_SKY_DARK,
										strokeOpacity: 1,
										strokeWeight: 10,
										map: map,
										visible: true,
									}}
								/>
							</>
						)}
					</GoogleMap>
				)}
				{selectedCmsMapName && (
					<div className={styles.cardoverlaywrapper}>
						<CmsMapOverlayCard
							key={selectedCmsMap.id}
							title={selectedCmsMap.title}
							cmsMapName={selectedCmsMap.title}
							location={selectedCmsMap.field_map_card_location}
							ctaUrl={selectedCmsMap.field_link?.uri}
							ctaLabel={selectedCmsMap.field_link?.title}
							secondaryInfoTitle={selectedCmsMap.field_secondary_info_title}
							secondaryInfoText={selectedCmsMap.field_secondary_info_text}
							subTitle={selectedCmsMap.field_subtitle}
							tertiaryInfoText={selectedCmsMap.field_tertiary_info_text?.value}
							googleMapsUrl={selectedCmsMap.field_google_maps_link}
							shouldOpenModal={!!selectedCmsMap.field_tertiary_info_text?.value}
							onClose={() => onSelectCmsMap(null)}
						/>
					</div>
				)}
			</div>
		</div>
	);
};

export default withSkipMap(CmsMap);
